import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import filesize from 'filesize';
import { format, isValid } from 'date-fns';
import { Box, Button, Divider, Stack, TextField } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import LoadingButton from '@mui/lab/LoadingButton';
import { MdDeleteForever, MdFileDownload, MdVisibility } from 'react-icons/md';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBRLocale from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useManager } from '../../../../../../hooks/Manager';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import {
  ModalTitle,
  ModalText,
  LabelFileInput,
  FileInput,
  IconButton
} from './styled';
import { MyCollectionsContext } from '../..';

export const ModalUploadInvoices = () => {
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [acceptFiles, setAcceptFiles] = useState([]);
  const [invoiceNumberErrorList, setInvoiceNumberErrorList] = useState([]);
  const [invoiceDateErrorList, setInvoiceDateErrorList] = useState([]);
  const [removeInvoice, setRemoveInvoice] = useState('');
  const [confirmRemoveInvoice, setConfirmRemoveInvoice] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [uploadConfig, setUploadConfig] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputFileRef = useRef(null);

  const { getPickupManagerBranchDocumentation } = useManager();
  const { closeModal, Modal } = useModal();
  const { uploadTaxInvoice } = usePickup();

  const {
    protocolToModal,
    setProtocolToModal,
    pickupIdToModal,
    setPickupIdToModal,
    branchParameterizationToModal,
    setBranchParameterizationToModal,
    limitToUploadInvoice,
    refreshSearch
  } = useContext(MyCollectionsContext);

  const maxXS = useMediaQuery('(max-width:480px)');
  const maxSM = useMediaQuery('(max-width:620px)');

  const handleAddInvoiceFiles = (event) => {
    const { files } = event.target;

    const newInvoiceFiles = [...invoiceFiles];

    let hasInvalidFile = false;
    let hasMoreFileThanExpect = false;

    Array.from(files).forEach((file) => {
      if (file.type !== 'application/pdf' && file.type !== 'text/xml') {
        hasInvalidFile = true;
        return;
      }

      if (!uploadConfig?.enableDanfeUpload && file.type === 'application/pdf') {
        hasInvalidFile = true;
        return;
      }

      if (!uploadConfig?.enableXmlUpload && file.type === 'text/xml') {
        hasInvalidFile = true;
        return;
      }

      if (newInvoiceFiles?.length >= limitToUploadInvoice) {
        hasMoreFileThanExpect = true;
        return;
      }

      newInvoiceFiles.push({
        id: uuid(),
        file,
        name: file.name,
        size: file.size,
        readableSize: filesize(file.size),
        invoiceNumber: '',
        invoiceDate: ''
      });
    });

    hasInvalidFile &&
      toast(
        <Toast
          title="Atenção!"
          content="Um dos arquivos selecionados possui formato inválido."
          type="error"
        />,
        options
      );

    hasMoreFileThanExpect &&
      toast(
        <Toast
          title="Atenção!"
          content={`A coleta está aguardando somente ${limitToUploadInvoice} nota${
            limitToUploadInvoice > 1 ? 's' : ''
          } fisca${limitToUploadInvoice > 1 ? 'is' : 'l'}.`}
          type="error"
        />,
        options
      );

    setInvoiceFiles(newInvoiceFiles);

    inputFileRef.current.value = '';
  };

  const handleRemoveInvoice = () => {
    const newInvoiceFiles = [
      ...invoiceFiles.filter((file) => file.id !== removeInvoice)
    ];

    setInvoiceFiles(newInvoiceFiles);

    setRemoveInvoice('');
  };

  const handleSetInvoiceNumber = (event, id) => {
    const { value: invoiceNumber } = event.target;

    const indexFile = invoiceFiles.findIndex((file) => file.id === id);

    const newInvoiceFiles = [...invoiceFiles];

    newInvoiceFiles[indexFile].invoiceNumber = invoiceNumber;

    setInvoiceFiles(newInvoiceFiles);
  };

  const handleSetInvoiceDate = (invoiceDate, id) => {
    const indexFile = invoiceFiles.findIndex((file) => file.id === id);

    const newInvoiceFiles = [...invoiceFiles];

    newInvoiceFiles[indexFile].invoiceDate = invoiceDate;

    setInvoiceFiles(newInvoiceFiles);
  };

  const handleOpenInvoiceFile = (invoiceFile) => {
    const file = new Blob([invoiceFile], { type: invoiceFile.type });
    const fileURL = URL.createObjectURL(file);
    const pdfWindow = window.open();
    pdfWindow.location.href = fileURL;
  };

  const handleDownloadInvoiceFile = (invoiceFile) => {
    const file = new Blob([invoiceFile], { type: invoiceFile.type });
    const fileURL = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', invoiceFile.name);

    document.body.appendChild(link);

    link.click();

    link.parentNode.removeChild(link);
  };

  const handleCloseModal = () => {
    setProtocolToModal('');
    setPickupIdToModal('');
    setBranchParameterizationToModal('');
    closeModal();
  };

  const handleBack = () => {
    invoiceFiles.length > 0 ? setConfirmClose(true) : handleCloseModal();
  };

  const handleUpload = async () => {
    let hasMissingInvoiceFile = false;
    let hasMissingInvoiceNumber = false;
    let hasInvalidInvoiceNumber = false;
    let hasMissingDate = false;
    let hasInvalidDate = false;

    if (invoiceFiles.length > 0) {
      const newInvoiceNumberErrorList = [...invoiceNumberErrorList];
      const newInvoiceDateErrorList = [...invoiceDateErrorList];

      invoiceFiles.forEach((invoice) => {
        if (
          invoice.file &&
          (!invoice.invoiceNumber || invoice.invoiceNumber === '')
        ) {
          hasMissingInvoiceNumber = true;

          !newInvoiceNumberErrorList.includes(invoice.id) &&
            newInvoiceNumberErrorList.push(invoice.id);
        } else if (invoice.invoiceNumber <= 0) {
          hasInvalidInvoiceNumber = true;

          !newInvoiceNumberErrorList.includes(invoice.id) &&
            newInvoiceNumberErrorList.push(invoice.id);
        } else {
          const index = newInvoiceNumberErrorList.findIndex(
            (id) => id === invoice.id
          );

          index >= 0 && newInvoiceNumberErrorList.splice(index, 1);
        }

        if (invoice.invoiceDate === '' || invoice.invoiceDate === null) {
          hasMissingDate = true;

          !newInvoiceDateErrorList.includes(invoice.id) &&
            newInvoiceDateErrorList.push(invoice.id);
        } else if (
          !isValid(invoice.invoiceDate) ||
          format(new Date(invoice.invoiceDate), 'yyyy')?.replace(/^0+/, '')
            ?.length < 4
        ) {
          hasInvalidDate = true;

          !newInvoiceDateErrorList.includes(invoice.id) &&
            newInvoiceDateErrorList.push(invoice.id);
        } else {
          const index = newInvoiceDateErrorList.findIndex(
            (id) => id === invoice.id
          );

          index >= 0 && newInvoiceDateErrorList.splice(index, 1);
        }
      });

      setInvoiceNumberErrorList(newInvoiceNumberErrorList);
      setInvoiceDateErrorList(newInvoiceDateErrorList);

      hasMissingInvoiceNumber &&
        toast(
          <Toast
            title="Atenção!"
            content="Informe o número de todas notas fiscais."
            type="error"
          />,
          options
        );

      hasInvalidInvoiceNumber &&
        toast(
          <Toast
            title="Atenção!"
            content="Número inválido informado em uma das notas fiscais."
            type="error"
          />,
          options
        );

      hasMissingDate &&
        toast(
          <Toast
            title="Atenção!"
            content="Informe a data de todas notas fiscais."
            type="error"
          />,
          options
        );

      hasInvalidDate &&
        toast(
          <Toast
            title="Atenção!"
            content="Data inválida informada em uma das notas fiscais."
            type="error"
          />,
          options
        );
    } else {
      hasMissingInvoiceFile = true;

      toast(
        <Toast
          title="Atenção!"
          content="Não foi anexado nenhum documento."
          type="error"
        />,
        options
      );
    }

    if (
      hasMissingInvoiceFile ||
      hasMissingInvoiceNumber ||
      hasInvalidInvoiceNumber ||
      hasMissingDate ||
      hasInvalidDate
    ) {
      return;
    }

    try {
      setIsSubmitting(true);

      let firstFile = true;
      let hasFailedUploadInvoice = false;

      // eslint-disable-next-line no-restricted-syntax
      for await (const invoiceFile of invoiceFiles) {
        const data = {
          pickupId: pickupIdToModal,
          file: invoiceFile.file,
          number: invoiceFile.invoiceNumber,
          dateIssue: new Date(invoiceFile.invoiceDate).toISOString()
        };

        if (firstFile) {
          data.taxQuantity = invoiceFiles.length;
          firstFile = false;
        }

        const uploadTaxInvoiceResponse = await uploadTaxInvoice({
          data
        });

        if (uploadTaxInvoiceResponse.status !== 200) {
          hasFailedUploadInvoice = true;
        }
      }

      if (hasFailedUploadInvoice) {
        toast(
          <Toast
            title="Atenção!"
            content="Falha ao anexar uma ou mais notas fiscais."
            type="error"
          />,
          options
        );
      } else {
        toast(
          <Toast
            title="Sucesso!"
            content="Notas fiscais anexadas com sucesso."
            type="success"
          />,
          options
        );
      }

      handleCloseModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      toast(
        <Toast
          title="Atenção!"
          content="Falha ao anexar notas fiscais."
          type="error"
        />,
        options
      );
    } finally {
      setIsSubmitting(false);
      refreshSearch();
    }
  };

  const onComponentDidMount = async () => {
    try {
      const documentationResponse = await getPickupManagerBranchDocumentation({
        branchParameterizationId: branchParameterizationToModal
      });

      if (documentationResponse?.length > 0) {
        const { enableXmlUpload, enableDanfeUpload } = documentationResponse[0];

        const newAcceptFiles = [];

        enableXmlUpload && newAcceptFiles.push('.xml');
        enableDanfeUpload && newAcceptFiles.push('.pdf');

        setAcceptFiles(newAcceptFiles);

        setUploadConfig({ enableXmlUpload, enableDanfeUpload });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    onComponentDidMount();
  }, []);

  return (
    <Modal preventClose>
      {!confirmClose && !confirmRemoveInvoice && (
        <Stack spacing={2}>
          <ModalTitle>Upload de Notas fiscais</ModalTitle>

          <Stack
            spacing={2}
            style={{
              maxHeight: '86vh',
              overflow: 'auto'
            }}
          >
            <ModalText>Protocolo: {protocolToModal}</ModalText>

            <Box style={{ display: 'flex' }}>
              <LabelFileInput
                htmlFor="invoice-files"
                style={{
                  width: '100%',
                  color:
                    isLoadingDetails ||
                    isSubmitting ||
                    acceptFiles?.length === 0 ||
                    invoiceFiles?.length >= limitToUploadInvoice
                      ? '#bdbdbd'
                      : '#e38500',
                  backgroundColor:
                    isLoadingDetails ||
                    isSubmitting ||
                    acceptFiles?.length === 0 ||
                    invoiceFiles?.length >= limitToUploadInvoice
                      ? '#ffffff'
                      : 'ffffff',
                  border:
                    isLoadingDetails ||
                    isSubmitting ||
                    acceptFiles?.length === 0 ||
                    invoiceFiles?.length >= limitToUploadInvoice
                      ? '1px solid #bdbdbd'
                      : '1px solid rgba(227, 133, 0, 0.5)',
                  cursor:
                    isLoadingDetails ||
                    isSubmitting ||
                    acceptFiles?.length === 0 ||
                    invoiceFiles?.length >= limitToUploadInvoice
                      ? 'default'
                      : 'pointer'
                }}
                disabled={
                  isLoadingDetails ||
                  isSubmitting ||
                  acceptFiles?.length === 0 ||
                  invoiceFiles?.length >= limitToUploadInvoice
                }
              >
                ANEXAR DANFE (PDF ou XML)
              </LabelFileInput>
              <FileInput
                ref={inputFileRef}
                id="invoice-files"
                type="file"
                accept={acceptFiles?.length > 0 ? acceptFiles.join(', ') : ''}
                multiple
                onChange={handleAddInvoiceFiles}
                disabled={
                  isLoadingDetails ||
                  isSubmitting ||
                  acceptFiles?.length === 0 ||
                  invoiceFiles?.length >= limitToUploadInvoice
                }
              />
            </Box>

            {invoiceFiles.length > 0 && (
              <Divider orientation="horizontal" flexItem />
            )}

            <Stack
              gap={2}
              divider={<Divider orientation="horizontal" flexItem />}
            >
              {invoiceFiles.map(
                ({
                  id,
                  file,
                  name,
                  readableSize,
                  invoiceNumber,
                  invoiceDate
                }) => {
                  if (file) {
                    return (
                      <Stack key={id} gap={2} style={{ width: '100%' }}>
                        <Stack direction={maxXS ? 'column' : 'row'} gap={2}>
                          <TextField
                            label="Número Nota Fiscal *"
                            size="small"
                            disabled={isSubmitting}
                            value={invoiceNumber}
                            onChange={(event) => {
                              const regex = /^[0-9]+$/;
                              if (
                                !event.target.value.match(regex) &&
                                event.target.value !== ''
                              ) {
                                return;
                              }
                              handleSetInvoiceNumber(event, id);
                            }}
                            inputProps={{ maxLength: 10 }}
                            style={{ width: maxXS ? '100%' : 256 }}
                            error={invoiceNumberErrorList.includes(id)}
                          />

                          <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            locale={ptBRLocale}
                          >
                            <DatePicker
                              label="Data de saída *"
                              mask="__/__/____"
                              disabled={isSubmitting}
                              value={invoiceDate}
                              onChange={(value) => {
                                handleSetInvoiceDate(value, id);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  size="small"
                                  {...params}
                                  onBlur={(event) => {
                                    if (
                                      event?.target?.value?.length >= 8 &&
                                      event?.target?.value?.length < 10
                                    ) {
                                      const [day, month, year] =
                                        event.target.value.split('/');

                                      const newDate = new Date(
                                        `20${year?.replace(/^0+/, '')}`,
                                        month - 1,
                                        day
                                      );

                                      handleSetInvoiceDate(newDate, id);
                                    }
                                  }}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: 'DD/MM/AAAA'
                                  }}
                                  style={{ width: maxXS ? '100%' : 192 }}
                                  error={invoiceDateErrorList.includes(id)}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </Stack>

                        <Stack
                          direction={maxXS ? 'column' : 'row'}
                          gap={maxXS && 1}
                          style={{
                            width: maxSM ? '100%' : 464,
                            display: 'flex',
                            flexDirection: maxXS ? 'column' : 'row',
                            alignItems: maxXS ? 'flex-start' : 'center',
                            justifyContent: maxXS ? 'center' : 'space-between'
                          }}
                        >
                          <Box
                            style={{
                              maxWidth: maxXS ? '100%' : 320,
                              display: 'flex',
                              flexDirection: 'column',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <strong
                              style={{
                                fontSize: 14,
                                width: maxXS ? '60vw' : 320,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {name}
                            </strong>
                            <span
                              style={{
                                fontSize: 12,
                                color: '#999',
                                marginTop: 4
                              }}
                            >
                              {readableSize}
                            </span>
                          </Box>

                          <Stack
                            direction="row"
                            gap={1}
                            style={{
                              width: 112,
                              alignSelf: maxXS && 'center'
                            }}
                          >
                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => handleOpenInvoiceFile(file)}
                            >
                              <MdVisibility size={20} />
                            </IconButton>

                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => handleDownloadInvoiceFile(file)}
                            >
                              <MdFileDownload size={20} />
                            </IconButton>

                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => {
                                setRemoveInvoice(id);
                                setConfirmRemoveInvoice(true);
                              }}
                            >
                              <MdDeleteForever size={20} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  }
                  return null;
                }
              )}
              {invoiceFiles.length === 0 && (
                <Stack style={{ width: !maxXS && 464, alignItems: 'center' }}>
                  <span>Sem arquivos selecionados</span>
                </Stack>
              )}
            </Stack>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="button"
                variant="contained"
                onClick={handleUpload}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Salvar
              </LoadingButton>
            </Box>
          </Stack>
        </Stack>
      )}

      {confirmRemoveInvoice && (
        <Stack spacing={2}>
          <ModalTitle>Atenção</ModalTitle>

          <ModalText>
            O arquivo anexado será excluído da solicitação. <br /> Deseja
            Continuar?
          </ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => {
                setRemoveInvoice('');
                setConfirmRemoveInvoice(false);
              }}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                handleRemoveInvoice();
                setConfirmRemoveInvoice(false);
              }}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}

      {confirmClose && (
        <Stack spacing={2}>
          <ModalTitle>Atenção</ModalTitle>

          <ModalText>Deseja cancelar o upload de notas fiscais?</ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => setConfirmClose(false)}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => handleCloseModal()}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}
    </Modal>
  );
};
